import type { VenueOSClient } from '@mappedin/venueos-applet';
import { useEffect, useRef, useState } from 'react';

/**
 * A Mappedin APIv1 event, as returned by `/api/mappedin/apiv1/1/event?venue=<slug>`.
 *
 * The proxy forwards the upstream APIv1 payload as-is. Different venues
 * surface different fields and different shapes for the location
 * relation, so the renderer accepts all of:
 *   - `locationName` / `locationId` flattened siblings
 *   - `location` as a string ID or `{ id, name }` object
 *   - `locations` array of strings or `{ id, name }` objects
 * and tries each in turn before giving up.
 *
 * Image URL similarly varies between `image`, `picture`, `imageUrl`, and
 * `logo` depending on whether the entry was authored as an event,
 * promotion, deal, or ad.
 */
export interface MapEventLocationRef {
  id?: string;
  name?: string;
}

/**
 * Mappedin APIv1 image fields are returned either as a plain URL string
 * (`"https://..."`) or as an object with size variants
 * (`{ original, thumbnail?, ... }`). The renderer reads `original` first
 * and falls back to any other string-valued field on the object so a
 * thumbnail-only entry still renders.
 */
export type MapEventImage = string | { original?: string; [key: string]: unknown };

export interface MapEvent {
  id?: string;
  name?: string;
  type?: string;
  image?: MapEventImage;
  picture?: MapEventImage;
  imageUrl?: MapEventImage;
  logo?: MapEventImage;
  description?: string;
  startDate?: string | number;
  endDate?: string | number;
  showDate?: string | number;
  location?: string | MapEventLocationRef;
  locations?: Array<string | MapEventLocationRef>;
  locationId?: string;
  locationName?: string;
  [key: string]: unknown;
}

interface UseMapEventsProps {
  client: VenueOSClient | null;
  venueSlug: string | null;
}

interface UseMapEventsReturn {
  events: MapEvent[];
  isLoading: boolean;
  error: string | null;
}

function parseEvents(raw: unknown): MapEvent[] {
  if (!Array.isArray(raw)) return [];
  const events: MapEvent[] = [];
  for (const entry of raw) {
    if (entry && typeof entry === 'object') {
      events.push(entry as MapEvent);
    }
  }
  return events;
}

/**
 * Fetch a venue's events from the Mappedin CMS via the APIv1 proxy.
 *
 * Replaces the App Builder's analytics-backed `select-event`/`view-event`
 * aggregation. Events are part of the venue's CMS-authored map data,
 * so they don't need `analytics:read` — the route only needs
 * `map:read` on the applet's permission set.
 */
export function useMapEvents({ client, venueSlug }: UseMapEventsProps): UseMapEventsReturn {
  const [events, setEvents] = useState<MapEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!client || !venueSlug) {
      setEvents([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    const reqId = ++reqIdRef.current;
    setIsLoading(true);
    setError(null);

    const params = new URLSearchParams({ venue: venueSlug });
    const path = `/api/mappedin/apiv1/1/event?${params.toString()}`;
    void client.api
      .fetch(path)
      .then((response) => {
        if (reqId !== reqIdRef.current) return;
        if (!response.ok) {
          setEvents([]);
          setError(`Failed to load events (${response.status})`);
          setIsLoading(false);
          return;
        }
        setEvents(parseEvents(response.data));
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (reqId !== reqIdRef.current) return;
        setEvents([]);
        setError(err instanceof Error ? err.message : 'Failed to load events');
        setIsLoading(false);
      });
  }, [client, venueSlug]);

  return { events, isLoading, error };
}
